import React from 'react';
import { VoucherTransaction } from '../../features/voucher/types';

interface ERupiVoucherTransactionHistoryProps {
  transactions: VoucherTransaction[];
  formatDate: (dateString: string | null | undefined) => string;
  formatAmount: (amount: number | string | null | undefined) => string;
  currencySymbol: string;
}

const STATUS_BADGE: Record<NonNullable<VoucherTransaction['status']>, { label: string; className: string }> = {
  SUCCEEDED: { label: 'Succeeded', className: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
  PARTIALLY_REVERSED: { label: 'Partially reversed', className: 'bg-amber-100 text-amber-800' },
  REVERSED: { label: 'Reversed', className: 'bg-gray-200 text-gray-700' },
};

const ERupiVoucherTransactionHistory: React.FC<ERupiVoucherTransactionHistoryProps> = ({
  transactions,
  formatDate,
  formatAmount,
  currencySymbol,
}) => {
  // Only MYP transactions carry a status; the India layout (VPA column, no
  // status/reference) is left exactly as it was.
  const showStatus = transactions.some((t) => t.status);

  // Date and time in the viewer's local timezone; falls back to the plain
  // date if the backend didn't send a timestamp.
  const formatDateTime = (t: VoucherTransaction): string => {
    const parsed = t.transactionDateTime ? new Date(t.transactionDateTime) : null;
    if (!parsed || isNaN(parsed.getTime())) return formatDate(t.transactionDate);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
  };

  return (
    <div className="p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Transaction History</h2>

      {transactions.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border border-gray-100 rounded-lg">
          No transactions found.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Transaction Date</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                  {showStatus ? (
                    <>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Reference</th>
                    </>
                  ) : (
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">VPA</th>
                  )}
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Payee Name</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => {
                  const badge = transaction.status ? STATUS_BADGE[transaction.status] : null;
                  return (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                      }`}
                    >
                      <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                        {formatDateTime(transaction)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900">
                        {currencySymbol}{formatAmount(transaction.amount)}
                        {!!transaction.reversedAmount && transaction.reversedAmount > 0 && (
                          <div className="text-xs font-normal text-amber-700">
                            {currencySymbol}{formatAmount(transaction.reversedAmount)} reversed
                          </div>
                        )}
                      </td>
                      {showStatus ? (
                        <>
                          <td className="py-3 px-4">
                            {badge ? (
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                                {badge.label}
                              </span>
                            ) : '-'}
                            {transaction.status === 'FAILED' && transaction.reasonText && (
                              <div className="text-xs text-gray-500 mt-1">{transaction.reasonText}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600 font-mono text-xs break-all">
                            {transaction.reference || '-'}
                          </td>
                        </>
                      ) : (
                        <td className="py-3 px-4 text-gray-600">
                          {transaction.vpa || '-'}
                        </td>
                      )}
                      <td className="py-3 px-4 text-gray-600">
                        {transaction.payeeName || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERupiVoucherTransactionHistory;
